<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.activation_status') }}
              </span>
            </p>
            <b-select v-model="filterStatus">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="1">{{ $t('attributes.enabled') }}</option>
              <option value="0">{{ $t('attributes.disabled') }}</option>
            </b-select>
          </b-field>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.main_system') }}
              </span>
            </p>
            <b-select v-model="filterMainSystem">
              <option v-for="main in mainItems" :key="main.id" :value="main.id">
                {{ main.title }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <b-input
              v-model="search"
              placeholder="Search..."
              type="search"
              icon="magnify"
            ></b-input>
            <p class="control">
              <button class="button is-primary">
                {{ $t('attributes.search') }}
              </button>
            </p>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button class="is-primary" icon-left="plus" @click="onAdd">
            {{ $t('attributes.add') + $t('attributes.sub_system') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="data"
      :loading="isLoading"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      :hoverable="true"
      :narrowed="true"
      :mobile-cards="true"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
    >
      <b-table-column
        field="status"
        :label="$t('attributes.activation_status')"
        width="95"
        sortable
        v-slot="props"
      >
        <b-icon :icon="props.row.status === '1' ? 'check' : ''"></b-icon>
      </b-table-column>

      <b-table-column
        field="id"
        :label="$t('attributes.id')"
        width="95"
        numeric
        sortable
        v-slot="props"
      >
        {{ props.row.id }}
      </b-table-column>

      <b-table-column
        field="sort"
        :label="$t('attributes.sort')"
        width="95"
        numeric
        sortable
        v-slot="props"
      >
        {{ props.row.sort }}
      </b-table-column>

      <b-table-column
        field="is_menu"
        :label="$t('attributes.is_menu')"
        width="160"
        sortable
        centered
        v-slot="props"
      >
        {{ whetherStatus(props.row.is_menu) }}
      </b-table-column>

      <b-table-column
        field="version"
        :label="$t('attributes.version')"
        width="95"
        numeric
        sortable
        v-slot="props"
      >
        {{ props.row.version }}
      </b-table-column>

      <b-table-column
        field="title"
        :label="$t('attributes.title')"
        sortable
        v-slot="props"
      >
        {{ props.row.title }}
      </b-table-column>

      <b-table-column
        field="full_path"
        :label="$t('attributes.system_name')"
        sortable
        v-slot="props"
      >
        {{ props.row.main_system_path
        }}<span class="has-text-primary has-text-weight-bold">{{
          props.row.path
        }}</span>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.function')"
        width="75"
        v-slot="props"
      >
        <b-button type="is-info" @click.prevent="onModify(props.row)">
          {{ $t('attributes.modify') }}
        </b-button>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
            <p>{{ $t('attributes.empty') }}</p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal :active.sync="isModalActive" has-modal-card>
      <sub-system-form
        v-bind="selected"
        :isLoading="isLoading"
        :main-systems="mainItems"
        :main-system-id="filterMainSystem"
        @onSend="onSend"
      />
    </b-modal>
  </section>
</template>

<script>
import { mapActions } from 'vuex'
import { SYSTEM_MAIN_SYSTEM_API, SYSTEM_SUB_SYSTEM_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import SubSystemForm from '@/components/system/SubSystemForm'

export default {
  name: 'SubSystems',
  components: {
    SubSystemForm
  },

  data() {
    return {
      isLoading: false,
      mainItems: [],
      items: [],
      sortField: 'sort',
      sortOrder: 'asc',
      defaultSortOrder: 'asc',
      isModalActive: false,
      search: '',
      filterStatus: '',
      filterMainSystem: null,
      selected: null,
      isEditing: false
    }
  },
  computed: {
    data() {
      let items = [...this.items]

      if (this.filterStatus !== '') {
        items = items.filter(s => s.status === this.filterStatus)
      }

      if (this.filterMainSystem !== '') {
        items = items.filter(s => s.main_system_id === this.filterMainSystem)
      }

      if (this.search !== '') {
        items = items.filter(s => s.title.includes(this.search))
      }

      return items
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    ...mapActions('app', ['fetchPermissions']),
    fetchData() {
      const promises = [
        this.axios.get(SYSTEM_MAIN_SYSTEM_API),
        this.axios.get(SYSTEM_SUB_SYSTEM_API)
      ]

      this.isLoading = true
      return Promise.all(promises)
        .then(([mainRes, subRes]) => {
          this.mainItems = mainRes.data.data.sort((x1, x2) => x1.sort - x2.sort)
          this.items = subRes.data.data.map(item => {
            return this.collectSystemInfo(item, this.mainItems)
          })
          this.filterMainSystem =
            this.mainItems.length > 0 ? this.mainItems[0].id : null
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    onAdd() {
      this.selected = {}
      this.isModalActive = true
    },
    onModify(item) {
      this.selected = item
      this.isModalActive = true
    },
    onSend(item) {
      if (item.id == null) {
        this.createSubSystem(item)
      } else {
        this.updateSubSystem(item)
      }
    },
    createSubSystem(item) {
      this.isLoading = true
      this.axios
        .post(SYSTEM_SUB_SYSTEM_API, item)
        .then(res => {
          const _item = this.collectSystemInfo(res.data.data, this.mainItems)

          this.items = [...this.items, _item]
          this.isModalActive = false

          this.fetchPermissions()

          notifyService.success(this.$t('attributes.add_succeed'))
        })
        .catch(e => {
          notifyService.create_error(e)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    updateSubSystem(item) {
      const api = `${SYSTEM_SUB_SYSTEM_API}/${item.id}`

      this.isLoading = true
      this.axios
        .put(api, item)
        .then(res => {
          const _item = this.collectSystemInfo(res.data.data, this.mainItems)

          const idx = this.items.findIndex(s => s.id === _item.id)
          const items = [...this.items]
          items[idx] = _item

          this.items = items
          this.isModalActive = false

          this.fetchPermissions()

          notifyService.success(this.$t('attributes.update_succeed'))
        })
        .catch(e => {
          notifyService.update_error(e)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    collectSystemInfo(item, mainItems) {
      const main = mainItems.find(m => m.id === item.main_system_id)
      const mainPath = main.name ? '/' + main.name : ''
      const subPath = item.name ? '/' + item.name : ''

      return {
        ...item,
        full_path: mainPath + subPath,
        path: subPath,
        main_system_path: mainPath,
        main_system_icon: main.icon
      }
    }
  }
}
</script>
